/**
 * All site copy lives here so it can be edited without touching components.
 *
 * TODO before go-live: replace every value in `company` marked PLACEHOLDER
 * with the real registered details. Nothing regulatory should be invented.
 */

export type Flow = 'practice' | 'willbank';

export const company = {
  name: 'Solace Partners',
  legalName: 'Solace Partners Limited', // PLACEHOLDER
  tagline: "We preserve your legacy and protect your clients' legacies.",
  email: 'jamespatten@solace.partners',
  // Kept as visibly unfinished text so no invented regulatory detail can ship.
  companyNumber: '[company number]', // PLACEHOLDER
  sraNumber: '[SRA number]', // PLACEHOLDER — the acquiring authorised body
  address: '[registered office address]', // PLACEHOLDER
};

export const flowMeta: Record<
  Flow,
  { id: Flow; slug: string; short: string; cta: string; noun: string }
> = {
  practice: {
    id: 'practice',
    slug: 'practice',
    short: 'Practice',
    cta: "I'm looking to sell my practice",
    noun: 'practice',
  },
  willbank: {
    id: 'willbank',
    slug: 'will-bank',
    short: 'Will bank',
    cta: "I'm looking to sell my will bank",
    noun: 'will bank',
  },
};

export const flowOrder: Flow[] = ['practice', 'willbank'];

export const navLinks = [
  { href: '#legacy', label: 'Your legacy' },
  { href: '#people', label: 'People & clients' },
  { href: '#how-it-works', label: 'How it works' },
  { href: '#compare', label: 'Will bank vs practice' },
  { href: '#faqs', label: 'FAQs' },
];

/* ---------------------------------------------------------------- hero --- */

export const hero = {
  eyebrow: 'Confidential · Regulated · No brokers',
  headlineLead: 'A new home for your',
  /** The emphasised part of the H1, which narrows once a flow is chosen. */
  headlineSubject: {
    undecided: 'Wills, Probate and Estate Planning Practice or Will Bank',
    practice: 'Wills, Probate and Estate Planning Practice',
    willbank: 'Will Bank',
  } as Record<Flow | 'undecided', string>,
  body: 'Solace Partners acquires wills, probate and estate planning practices and will banks from solicitors who are retiring, consolidating or stepping back from private client work. One conversation, a valuation you can see the workings of, and a handover your clients never feel as a disruption.',
  proofPoints: [
    'Indicative offer within 48 hours',
    'Will bank transfers in 4–6 weeks',
    'Practice sales in 8–12 weeks',
    'No listings, no brokers, no fees to you',
  ],
};

/* ------------------------------------------------------------- legacy --- */

export type Pillar = {
  icon: 'shield' | 'quill' | 'vault' | 'handshake' | 'people' | 'clock';
  title: string;
  body: string;
};

export const legacySection = {
  eyebrow: 'We protect your legacy',
  title: 'Decades of trust should not change hands carelessly',
  lead: 'You built a practice on being the person a family called when it mattered. That reputation is the asset. We treat it that way, and we put it in writing.',
  pillars: [
    {
      icon: 'quill',
      title: 'Your name can endure',
      body: 'Where you want it to, your firm continues as a trading style of the acquiring practice. Clients keep the letterhead they recognise and the local name they chose in the first place.',
    },
    {
      icon: 'shield',
      title: 'A dignified exit, on your terms',
      body: 'No public listing, no marketplace, no rumour circulating the local profession. You decide when the announcement is made and how it reads.',
    },
    {
      icon: 'handshake',
      title: 'Continuity of advice',
      body: 'Files, live matters and half-finished plans are picked up by qualified private client practitioners who do this work every day — not routed into a call centre queue.',
    },
    {
      icon: 'vault',
      title: 'The closing formalities handled',
      body: 'Run-off cover, file retention, SRA notifications and archive obligations are mapped out and costed at the offer stage, so nothing lands on you after completion.',
    },
  ] as Pillar[],
};

/* ------------------------------------------------- people and clients --- */

export const peopleSection = {
  eyebrow: 'We protect your employees and clients',
  title: 'The people who made the practice work are not the price of the deal',
  lead: 'Two groups have no say in a sale and the most to lose from a bad one. Both are looked after explicitly in the terms, not left to goodwill.',
  columns: [
    {
      title: 'Your employees',
      intro: 'Continuity of employment, and a bigger firm to grow into.',
      points: [
        'Employment transfers under TUPE with service, terms and pension arrangements preserved.',
        'Proper consultation. A sale is never used as cover for a quiet round of redundancies.',
        'Fee earners keep their caseloads and the client relationships they have built.',
        'Funded routes to STEP, CILEX and SQE qualification through our academy.',
        'No mandated relocation — local offices and hybrid working stay as they are.',
      ],
    },
    {
      title: 'Your clients',
      intro: 'One clear letter, the same standard of care, and a real choice.',
      points: [
        'Nobody is contacted until terms are signed and you have approved the wording.',
        'A warm letter of introduction, co-signed by you where you are comfortable with that.',
        'The same named contact wherever the handover allows it.',
        'Wills and deeds held in fire-rated, insured, individually indexed storage with same-day retrieval.',
        'Fees already quoted are honoured. A free review is offered, never pushed.',
      ],
    },
  ],
};

/* ------------------------------------------------------- how it works --- */

export type Step = {
  when: string;
  title: string;
  body: string;
};

export const howItWorks: Record<
  Flow,
  { title: string; lead: string; steps: Step[] }
> = {
  practice: {
    title: 'Selling your practice, step by step',
    lead: 'Eight to twelve weeks from first call to completion, with the regulatory workstream run alongside the deal rather than after it.',
    steps: [
      {
        when: 'Day one',
        title: 'A confidential call',
        body: 'Thirty minutes with a principal, not a broker or a business development manager. A mutual NDA is signed before anything sensitive is shared, and we tell you honestly if we are not the right buyer for you.',
      },
      {
        when: 'Week one',
        title: 'Information pack',
        body: 'Three years of accounts, work in progress and matter counts, a staff schedule, your lease and lock-up position, and the shape of the will bank. Enough to price properly, and no more than that.',
      },
      {
        when: 'Week one to two',
        title: 'Written indicative offer',
        body: 'A non-binding offer with the structure explained in plain terms: consideration, any earn-out and why, treatment of work in progress, and how the will bank has been valued separately.',
      },
      {
        when: 'Week two to three',
        title: 'Heads of terms',
        body: 'Price, timetable, warranties, and what happens to you afterwards — a consultancy day a week, a fixed handover period, or a clean break with a retirement announcement. Your choice, recorded in writing.',
      },
      {
        when: 'Week three to eight',
        title: 'Due diligence and regulatory',
        body: 'Legal, financial and compliance workstreams run in parallel. We take on the SRA notifications, the closure or transfer of the authorised body, and the six-year run-off cover arrangements with your broker.',
      },
      {
        when: 'Week eight to twelve',
        title: 'Completion and handover',
        body: 'Client letters go out, files and deeds transfer into indexed storage, your team onboards with their caseloads intact, and you stay as available as you want to be for the first ninety days.',
      },
    ],
  },
  willbank: {
    title: 'Selling your will bank, step by step',
    lead: 'Four to six weeks, and you carry on trading throughout. Only custody of the wills moves — your practice, your staff and your other work are untouched.',
    steps: [
      {
        when: 'Day one',
        title: 'A confidential call',
        body: 'Roughly how many wills you hold, the age profile of the testators, and whether you hold originals or copies. A mutual NDA first, and no obligation to share a single client name at this stage.',
      },
      {
        when: 'Day one to two',
        title: 'Will bank review',
        body: 'We look at volume, testator age and geography, how many clients also hold LPAs or trusts, the likely probate follow-on, and the condition and indexing of your storage.',
      },
      {
        when: 'Within 48 hours',
        title: 'Indicative offer',
        body: 'Priced on what is actually in your will bank rather than a per-will rule of thumb, with the workings shown. There is no broker margin sitting between you and the buyer.',
      },
      {
        when: 'Week one to three',
        title: 'Agreement and file schedule',
        body: 'A short transfer agreement, data processing terms, and an agreed schedule of files. We build the compliance trail as we go, so your COLP has a clean record of the decision.',
      },
      {
        when: 'Week four to six',
        title: 'Transfer and client letters',
        body: 'Deeds collected, reconciled against the schedule and indexed. Clients receive a professional letter introducing their new custodian and setting out their options. Then it is done.',
      },
    ],
  },
};

/* ----------------------------------------------------------- compare --- */

export type CompareRow = {
  label: string;
  willbank: string;
  practice: string;
};

export const compareSection = {
  eyebrow: 'Selling a will bank vs selling a practice',
  title: 'Two different transactions, often confused',
  lead: 'Most enquiries start with "I think I want to sell the wills" and turn out to be one or the other. This is the difference, plainly.',
  rows: [
    {
      label: 'What actually transfers',
      willbank:
        'Custody of the wills and deeds, and the client relationship for future estate work.',
      practice:
        'The whole business — goodwill, work in progress, files, staff, systems, and usually the will bank with it.',
    },
    {
      label: 'Do you keep practising?',
      willbank:
        'Yes. Many firms release the value in the will bank and carry on with conveyancing, litigation or commercial work.',
      practice:
        'Normally not. This is usually part of retirement, consolidation or an orderly closure.',
    },
    {
      label: 'Typical timescale',
      willbank: '4–6 weeks, with an indicative offer inside 48 hours.',
      practice: '8–12 weeks, depending on lease and diligence complexity.',
    },
    {
      label: 'Effect on your employees',
      willbank: 'None. No TUPE transfer and no restructuring.',
      practice:
        'Employment transfers under TUPE with continuity of service and terms.',
    },
    {
      label: 'Diligence burden on you',
      willbank: 'Light — a file schedule and your storage arrangements.',
      practice:
        'Full legal, financial and regulatory due diligence, run in parallel workstreams.',
    },
    {
      label: 'How it is priced',
      willbank:
        'On testator profile: volume, age, geography, asset complexity, and LPA or trust attachment.',
      practice:
        'A multiple of sustainable earnings, plus work in progress, plus the will bank valued in its own right.',
    },
    {
      label: 'SRA position',
      willbank:
        'A transfer of client files and papers to another authorised body. No closure to notify.',
      practice:
        'Formal closure or transfer of the authorised body, with notifications and run-off cover.',
    },
    {
      label: 'Your professional indemnity',
      willbank: 'Stays as it is — you are still trading.',
      practice:
        'Six-year run-off cover required. We model the cost into the offer and deal with your broker.',
    },
    {
      label: 'Choose this if',
      willbank:
        'You want to release value from a dormant asset without changing your business.',
      practice:
        'You are ready to step back, and you want your clients and your staff to land somewhere good.',
    },
  ] as CompareRow[],
};

/* --------------------------------------------------------------- faqs --- */

export type Faq = { q: string; a: string };
export type FaqGroup = { id: string; title: string; items: Faq[] };

export const faqSection = {
  eyebrow: 'FAQs',
  title: 'The questions that usually come before the decision',
  lead: 'Mostly about the regulator and about clients — which is exactly the right order to worry about them in.',
  groups: [
    {
      id: 'sra',
      title: 'SRA and regulation',
      items: [
        {
          q: 'Is selling a will bank SRA-compliant?',
          a: 'Yes. Transferring a will bank to another authorised body is a recognised and established mechanism under the SRA Standards and Regulations. The duties that matter are unchanged: act in each client’s best interests, keep them properly informed, and protect their confidentiality and their documents through the transfer. We build the compliance trail alongside the transaction — the basis for the transfer, the file schedule, data processing terms and the client correspondence — so your COLP has a clean, defensible record rather than a reconstruction after the fact.',
        },
        {
          q: 'Do I need to notify the SRA?',
          a: 'It depends which transaction you are doing. If you are selling only the will bank and continuing to trade, there is no closure to notify, though you should record the decision formally and update your file-retention policy and privacy notice. If you are selling the practice, the SRA must be notified of the closure or transfer of the authorised body, and there is a sequence to it — we run that workstream with you and work backwards from the date you want to stop practising.',
        },
        {
          q: 'What happens about run-off cover?',
          a: 'A firm that closes needs six years of run-off cover under the SRA Indemnity Insurance Rules, and it is frequently the largest single cost of retiring. It is also the thing people discover too late. We model it at the indicative offer stage and engage your broker early, so the number is on the table before heads of terms rather than after.',
        },
        {
          q: 'Who holds the files and retention obligations afterwards?',
          a: 'We take on custody and the retention obligations for everything that transfers, to the regulatory minimum periods and to our own longer policy for wills and deeds, which we retain indefinitely. Anything outside the deal stays with you, and we will tell you plainly and in writing which files fall on which side of that line.',
        },
        {
          q: 'Are you regulated, and by whom?',
          a: 'Acquisitions complete into an SRA-authorised body, so your clients’ files and papers never leave the regulated perimeter. We will give you the acquiring firm’s SRA number, its COLP and COFA contacts, and professional references from previous sellers on the first call, before you share anything with us.',
        },
      ],
    },
    {
      id: 'clients',
      title: 'Client notifications',
      items: [
        {
          q: 'How and when are my clients told?',
          a: 'Not until terms are signed and you have approved the wording, word for word. Clients then receive a single letter — on your letterhead, co-signed by you where you are willing — explaining that their will now has a new custodian, who to contact, and that nothing they have already arranged has changed. One clear communication, not a sequence of marketing touches.',
        },
        {
          q: 'Do clients have to consent to the transfer?',
          a: 'They are never trapped by it. The letter sets out plainly how to object, how to request their original will back, or how to move it to a solicitor of their own choosing, free of charge and without having to explain themselves. In practice very few do, because continuity with a regulated firm is what they wanted when they made the will in the first place.',
        },
        {
          q: 'Will my clients be cold-called or upsold?',
          a: 'No. We offer a free review because wills genuinely go stale — marriages, deaths, divorces, property, and changes like the residence nil-rate band — but it is an offer, not a campaign. There is no commission-led selling, no call centre, and clients can opt out of contact entirely and simply have their will held safely.',
        },
        {
          q: 'Where will the wills physically be held?',
          a: 'In fire-rated, flood-protected and insured storage, individually indexed, with a digital register mapped to the transfer schedule so any document can be located and retrieved within one working day. You get a copy of the reconciled schedule at completion.',
        },
        {
          q: 'What happens to clients who are mid-matter?',
          a: 'Live probate and estate administration files transfer with a named fee earner, and wherever possible with the person who has been handling the matter all along. Fees already quoted are honoured and existing plans are not re-papered to generate new work.',
        },
      ],
    },
    {
      id: 'practice',
      title: 'Your practice and your people',
      items: [
        {
          q: 'What actually happens to my staff?',
          a: 'Their employment transfers under TUPE with continuity of service, terms and pension arrangements intact. We consult properly and we do not use a sale as cover for redundancies. Beyond the legal position there is a commercial one: your fee earners hold the client relationships, and losing them would damage the thing we are buying.',
        },
        {
          q: 'Can I stay involved after completion?',
          a: 'Yes, and most principals choose some version of it — a consultancy day a week, a defined handover period, or a clean break with a proper retirement announcement. It is agreed at heads of terms and written down, so it does not quietly become open-ended.',
        },
        {
          q: 'Can my firm’s name continue?',
          a: 'Frequently, yes, as a trading style of the acquiring firm. Where a practice has carried a local reputation for thirty years, keeping the name is usually better for the clients, better for the staff and better for us.',
        },
        {
          q: 'My practice is not very profitable. Is it still worth a conversation?',
          a: 'Yes, and this is the most common reason people wait years longer than they needed to. A practice with thin margins can still hold a substantial will bank, a loyal client base and a capable team. Those are the assets we are actually valuing.',
        },
        {
          q: 'I am a will writer, not a solicitor. Can you still help?',
          a: 'Yes. We acquire will banks from unregulated will-writing firms, trust corporations and financial advice businesses as well as from SRA-regulated practices. The compliance steps differ — particularly around data protection and how the transfer is communicated — and we will walk you through what applies to you.',
        },
      ],
    },
    {
      id: 'commercial',
      title: 'Valuation, timing and cost',
      items: [
        {
          q: 'How is my will bank valued?',
          a: 'On what is in it, not on a per-will figure. The inputs are volume, the age profile of the testators, geography and likely asset complexity, whether you hold the originals or only copies, and how many clients also hold an LPA or a trust. We show you the workings — you should never have to take a valuation on faith.',
        },
        {
          q: 'How quickly can this be done?',
          a: 'An indicative offer on a will bank within 48 hours of the information pack, and completion in four to six weeks. A full practice sale typically completes in eight to twelve weeks, and faster where your accounts are in order and the lease position is straightforward.',
        },
        {
          q: 'What does it cost me?',
          a: 'Nothing to talk to us, and no broker fee, because there is no broker in the chain. You will have your own legal costs on a practice sale, and we agree at heads of terms who bears what, so there is no argument about it at completion.',
        },
        {
          q: 'Do I have to sell the whole thing?',
          a: 'No. Will bank only, a single department, or the entire practice. Some firms sell the private client side and keep commercial or property work running exactly as it was.',
        },
        {
          q: 'What if I am not ready yet?',
          a: 'Then have the conversation anyway and take a valuation away with you. Plenty of the people we speak to are eighteen months out and simply want to know what the number looks like and what they should tidy up before it counts.',
        },
      ],
    },
  ] as FaqGroup[],
};

/* ------------------------------------------------------------ contact --- */

export const contactSection = {
  eyebrow: 'Start a conversation',
  headings: {
    undecided: 'Start a confidential conversation',
    practice: 'Let’s talk about your practice',
    willbank: 'Let’s talk about your will bank',
  } as Record<Flow | 'undecided', string>,
  lead: 'One form, one reply from a principal, and a mutual NDA before anything sensitive changes hands. Nothing is shared, listed or discussed with anyone outside Solace Partners.',
  assurances: [
    'A principal replies within one working day',
    'Mutual NDA before any client or financial detail',
    'No brokers, no marketplace, no listing',
    'No obligation, and no follow-up if you ask us not to',
  ],
};
